export enum OrderStatus {
  CREATED = "created",
  WAITING_SELLER_CONFIRMATION = "waiting_seller_confirmation",
  READY_TO_PAY = "ready_to_pay",
  WAITING_PAYMENT_CONFIRMATION = "waiting_payment_confirmation",
  IN_ANALYSIS = "in_analysis",
  PAID = "paid",
  CANCELLED = "cancelled",
}

export enum DocumentType {
  CPF = "cpf",
  CNPJ = "cnpj",
}

export enum CnpjDiscountType {
  PERCENTAGE = "percentage",
  FIXED = "fixed",
}

export enum UserRole {
  ADMIN = "admin",
  COMPANY_ADMIN = "company_admin",
  COMPANY_SELLER = "company_seller",
  CUSTOMER = "customer",
}

export interface Company {
  id: number;
  name: string;
  code: string;
  bio?: string;
  logo_image?: Image;
  cover_image?: Image;
  banners: Image[];
  phone: string;
  minimun_pieces_number: number;
  minimun_order_value: number;
  is_payment_enabled: boolean;
  require_login_to_buy: boolean;
  cash_payment_discount_percentage: number;
  cnpj_discount_type?: CnpjDiscountType;
  cnpj_discount_percentage?: number;
  cnpj_discount_value?: number;
  is_product_observations_enabled: boolean;
  is_shipment_enabled: boolean;
  requires_birthdate?: boolean;
  is_maintenance_mode_enabled: boolean;
  requires_document?: boolean;
  requires_customer_address?: boolean;
  requires_customer_phone_on_register?: boolean;
  maintenance_mode_message?: string;
  whatsapp_button_url?: string;
  is_whatsapp_button_enabled?: boolean;
  default_seller?: User;
  is_excursion_info_required?: boolean;
  is_pickup_enabled?: boolean;
  show_stock_to_customer?: boolean;
  gtm_container_id?: string;
  meta_pixel?: string;
}

export interface Category {
  id: number;
  name: string;
  children: Category[];
  parent?: Category;
}

export interface Product {
  id: number;
  name: string;
  ref?: string;
  description: string;
  on_sale: boolean;
  price: number | null;
  lowest_price: number | null;
  highest_price: number | null;
  categories: Category[];
  images: Image[];
  variations: ProductVariation[];
  stock_quantity: number;
  stock_enabled: boolean;
}

export interface Color {
  id: number;
  name: string;
  image: Image;
}

export interface Size {
  id: number;
  name: string;
}

export interface ProductVariation {
  id: number;
  sku?: string;
  price: number;
  color: Color;
  size: Size;
  stock: number;
}

export interface Order {
  id: number;
  code: string;
  status: OrderStatus;
  value: number;
  items: OrderItem[];
  emepag_order_code?: string;
  shipment_value?: number;
  customer_name?: string;
  customer_phone?: string;
  customer_birthdate?: string;
  observations?: string;
  discounts_value?: number;
  coupon?: Coupon;
}

export interface OrderItem extends DateProperties {
  price: number;
  color: string;
  size: string;
  quantity: number;
  product: Product;
  variation: ProductVariation;
  observations?: string;
}

export interface Image {
  id: number;
  base_url: string;
  key: string;
  deleted_at?: string;
}

export interface VariationsValuesPayload {
  variation: ProductVariation;
  quantity: number;
}

export interface SellerData {
  id: number;
  name: string;
  phone?: string;
}

/* OrderProduct is different of OrderItem because it groups OrderItems's products */
export type OrderProduct = Product & {
  items: OrderItem[];
};

export enum ShipmentStatus {
  CREATED = "created",
  SENT = "sent",
  DELIVERED = "delivered",
  CANCELLED = "cancelled",
}

export enum ShippingMethod {
  EXCURSION = "excursion",
  CORREIOS = "correios",
  CUSTOM = "custom",
  SHIPPING_COMPANY = "shipping_company",
  PICKUP = "pickup",
}

export interface DateProperties {
  created_at: Date;
  updated_at: Date;
  deleted_at?: Date;
}

export interface CouponConditions {
  min_order_value?: number;
  min_items_count?: number;
  payment_method?: string;
  shipping_method?: string;
  region_city?: string;
  region_state?: string;
  per_user_limit?: number;
  expires_at?: string;
  first_order_only?: boolean;
  usage_limit?: number;
  usage_per_user?: number;
}

export interface Coupon {
  id: number;
  code: string;
  conditions: CouponConditions;
  description?: string;
  discount_percentage?: number;
  discount_fixed_value?: number;
  usage_count: number;
  active: boolean;
  max_discount_value: number;
}

export interface User {
  id: number;
  name: string;
  email: string;
  phone?: string;
  address?: UserAddress;
  role?: UserRole;
  birthdate?: string;
  document?: string;
}

export interface UserAddress {
  id: number;
  street: string;
  number: string;
  complement?: string;
  neighborhood: string;
  city: string;
  state: string;
  zip_code: string;
}
